import * as React from 'react'
import Link from 'next/link'
import styled from 'styled-components'
import { NextSeo } from 'next-seo'

import * as entity from '@boutique/entities'
import * as utils from '@boutique/utils'
import * as components from '@bob/components'
import { SITE_NAME, SITE_URL } from '@bob/config'
import * as ds from '@bob/design-system'
import * as url from '@bob/url'
import * as hooks from '@bob/hooks'

export type BrandListProps = {
  brands: entity.brand.MediumBrand[]
  index: hooks.useSearchEngine.Index
} & React.HTMLAttributes<HTMLDivElement>

const DEFAULT_PAGE_SIZE = 15

function BrandListComponent({ brands, className, index }: BrandListProps): React.ReactElement {
  const pageUrl = url.brands()

  const engine = hooks.useSearchEngine.useSearchEngine(brands, {
    index,
    identifier: (brand) => brand.slug,
    fullText: (query, brand) => {
      const normalizedQuery = utils.text.slugify(query)

      return (
        utils.text.slugify(brand.name).includes(normalizedQuery) ||
        utils.text.slugify(brand.descriptionShort).includes(normalizedQuery) ||
        utils.text.slugify(brand.creation.country)?.includes(normalizedQuery) ||
        utils.text.slugify(brand.creation.region ?? '')?.includes(normalizedQuery) ||
        utils.text.slugify(brand.creation.city ?? '')?.includes(normalizedQuery) ||
        brand.brandCategories.some((category) => category.name.includes(normalizedQuery)) ||
        brand.tags.some((tag) => utils.text.slugify(tag.name).includes(normalizedQuery))
      )
    },
    defaultQuery: {
      filters: {},
      text: '',
      sortType: hooks.useSearchEngine.SortType.AtoZ,
    },
    sorts: [
      {
        type: hooks.useSearchEngine.SortType.AtoZ,
        name: 'Nom (A à Z)',
        getTitle: (brand) => {
          return brand.name
        },
      },
      {
        type: hooks.useSearchEngine.SortType.ZtoA,
        name: 'Nom (Z à A)',
        getTitle: (brand) => {
          return brand.name
        },
      },
    ],
    defaultPage: 1,
    defaultSize: DEFAULT_PAGE_SIZE,
  })

  return (
    <>
      <NextSeo
        title={`${SITE_NAME} | Les marques engagées`}
        description="Découvrez toutes les marques bien pour la planète et les humains"
        canonical={SITE_URL + pageUrl.path}
        openGraph={{
          title: `${SITE_NAME} | Les marques`,
          description: 'Découvrez toutes les marques bien pour la planète et les humains',
          url: SITE_URL + pageUrl.path,
          images: [
            {
              alt: 'Bien ou Bien, le magasin où acheter des biens tout simplement bien',
              url: SITE_URL + '/assets/images/bob_og_image.png',
            },
          ],
        }}
        robotsProps={{
          maxImagePreview: 'large',
        }}
      />

      <ds.Container className={className}>
        <ds.BreadCrumbs crumbs={pageUrl.crumbs} />
        <components.ListingLayout.Heading
          color={ds.color.LIGHT_GREEN}
          description="Guide des marques par catégorie (mode, jouet ...), critères, etc."
          title={'Les marques engagées'}
        />

        <components.ListingLayout.DesktopContainer>
          <components.Sort engine={engine} label="marques triées par " />
        </components.ListingLayout.DesktopContainer>

        <components.ListingLayout.Wrapper>
          <components.ListingLayout.ScrollAnchor id="top" />
          <components.ListingLayout.Aside>
            <components.Search engine={engine} placeholder="Rechercher une marque" />
            <components.ListingLayout.Controls>
              <components.Filters engine={engine} />
              <components.ListingLayout.MobileContainer>
                <components.Sort engine={engine} label="marques triées par " />
              </components.ListingLayout.MobileContainer>
            </components.ListingLayout.Controls>
          </components.ListingLayout.Aside>
          <components.ListingLayout.Main>
            <components.ResultList engine={engine} loading={placeholders}>
              {({ item: brand }) => {
                return (
                  <components.ListingLayout.Item key={brand.slug}>
                    <Link
                      href={
                        url.brand({
                          slug: brand.slug,
                          name: brand.name,
                        }).path
                      }
                      prefetch={false}
                      legacyBehavior
                    >
                      <a>
                        <ds.BrandCard
                          nativeLazyLoad
                          coverImage={
                            // TODO: FIXME: Some images are missing data side, should not be the case
                            brand.coverImage?.src ?? ''
                          }
                          criteria={[...brand.pillars.environmental, ...brand.pillars.social].map((criterion) => ({
                            name: criterion.name,
                            icon: utils.symbols.getCriterionSymbolName(criterion.pillar.icon),
                          }))}
                          description={brand.descriptionShort}
                          logo={brand.logo}
                          name={brand.name}
                          signatory={brand.signatory}
                          tags={brand.brandCategories.map((tag) => tag.name)}
                        />
                      </a>
                    </Link>
                  </components.ListingLayout.Item>
                )
              }}
            </components.ResultList>
            <components.ListingLayout.Pagination>
              <components.Pagination engine={engine} />
            </components.ListingLayout.Pagination>
          </components.ListingLayout.Main>
        </components.ListingLayout.Wrapper>
      </ds.Container>
    </>
  )
}

export const BrandList = styled(BrandListComponent)`
  ${ds.LineGridLayout} {
    margin-bottom: 35px;
    margin-top: 20px;
  }
`

const placeholders = [...new Array(DEFAULT_PAGE_SIZE).keys()].map((index) => (
  <components.ListingLayout.Item key={`brand-skeleton-${index}`}>
    <ds.BrandCardSkeleton />
  </components.ListingLayout.Item>
))
